import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import axios from "../../utils/axios";
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Edit, Trash } from 'react-feather';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner
} from 'reactstrap';
import Swal from "sweetalert2";

const CourseCategories = (props) => {
    document.title = "Course Categories | Equity Circle - Admin Dashboard";
    const breadcrumbItems = [
        { title: "Courses Management", link: "#" },
        { title: "Course Categories", link: "#" },
    ];
    
    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        tags: ''
    });
    
    useEffect(() => {
        props.setBreadcrumbItems('Courses Management', breadcrumbItems);
        fetchCategories();
    }, []);
    
    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/education-categories`);
            setCategories(response.data.education_categories || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };
    
    const deleteCategory = async (id) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            });
            
            if (result.isConfirmed) {
                setLoading(true);
                await axios.delete(`/api/education/category/${id}`);
                toast.success('Category deleted successfully');
                fetchCategories();
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error('Failed to delete category');
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            // Convert tags string to array
            const payload = {
                ...formData,
                tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : []
            };
            
            if (editingCategoryId) {
                await axios.put(`/api/education/category/${editingCategoryId}`, payload);
                toast.success('Category updated successfully');
            } else {
                await axios.post('/api/education/category', payload);
                toast.success('Category added successfully');
            }
            setModal(false);
            setEditingCategoryId(null);
            setFormData({ name: '', tags: '' });
            fetchCategories();
        } catch (error) {
            console.error('Error adding category:', error);
            toast.error('Failed to add category');
            setLoading(false);
        }
    };
    
    const renderCategories = () => {
        if (loading && categories.length === 0) {
            return (
                <tr>
                    <td colSpan="3" className="text-center py-4">
                        <Spinner color="primary" />
                        <div className="mt-2">Loading categories...</div>
                    </td>
                </tr>
            );
        }

        if (categories.length === 0) {
            return (
                <tr>
                    <td colSpan="3" className="text-center py-4">
                        No categories found
                    </td>
                </tr>
            );
        }


        return categories.map((category) => (
            <tr key={category.id}>
                <td>{category.name}</td>
                <td>{category.tags || '-'}</td>
                <td>
                    <Button color="primary" size="sm" className="me-2" onClick={() => handleEdit(category)}>
                        <Edit size={14} />
                    </Button>
                    {/* <Button 
                        color="danger" 
                        size="sm" 
                        onClick={() => deleteCategory(category.id)}
                        disabled={loading}
                    >
                        <Trash size={14} />
                    </Button> */}
                </td>
            </tr>
        ));
    };
    
    const toggleModal = () => {
        if (!modal) {
            // Reset form when opening modal
            setFormData({ name: '', tags: '' });
            setEditingCategoryId(null);
        }
        setModal(!modal);
    };
    
    const handleEdit = (category) => {
        setFormData({
            ...category,
            tags: Array.isArray(category.tags) ? category.tags.join(', ') : (category.tags || '')
        });
        setEditingCategoryId(category.id);
        setModal(true);
    };
    
    return (
        <React.Fragment>
            <ToastContainer position="top-right" autoClose={5000} />
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <CardTitle tag="h4" className="mb-1">Course Categories</CardTitle>
                                    <p className="text-muted mb-0">
                                        Manage and view course categories.
                                    </p>
                                </div>
                                <Button 
                                    color="primary" 
                                    onClick={toggleModal}
                                    disabled={loading}
                                >
                                    <i className="bx bx-plus me-1"></i> Add New Category
                                </Button>
                            </div>
                            <Row>
                                <Col lg="12">
                                    <div className="table-responsive">
                                        <Table hover className="mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Tags</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderCategories()}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Add Category Modal */}
            <Modal isOpen={modal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>Add New Category</ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <FormGroup>
                            <Label for="name">Category Name</Label>
                            <Input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="tags">Tags (comma separated)</Label>
                            <Input
                                type="text"
                                name="tags"
                                id="tags"
                                value={formData.tags}
                                onChange={handleInputChange}
                                placeholder="e.g., finance, investment, trading"
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" color="secondary" onClick={toggleModal} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Category'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default connect(null, { setBreadcrumbItems })(CourseCategories);
